import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AddCircleOutline,
  CheckCircle,
  CloudUpload,
  ContentCopy,
  Delete as DeleteIcon,
  RestartAlt,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createAdminPreOrderItem,
  getAdminPreOrderItem,
  updateAdminPreOrderItem,
} from '../../services/preOrderService';
import { deleteUploadedImage, uploadManualCartImage } from '../../services/manualCart';

const previousKey = 'checkstall.preorder.previousSpecVariant';
const maxFileSize = 10 * 1024 * 1024;
const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const createImageId = () => `${Date.now()}-${Math.random()}`;
const createRowId = () => `${Date.now()}-${Math.random()}`;
const createRandomId = () => Math.floor(100000 + Math.random() * 900000).toString();
const emptySpec = () => ({
  id: createRowId(),
  name: '',
  value: '',
  price: '',
  quantity: '',
  imageUrl: '',
  variants: [],
});
const emptyVariant = () => ({
  id: createRowId(),
  name: '',
  value: '',
  price: '',
  quantity: '',
  imageUrl: '',
});

const PreOrderCreatePage = () => {
  const navigate = useNavigate();
  const { itemId } = useParams();
  const isEditing = Boolean(itemId);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    detail_url: '',
    pic_url: '',
    final_price: '',
    post_fee: '0',
    total_stock: '',
    max_per_user: '',
    status: 'active',
  });
  const [imageState, setImageState] = useState({
    files: [],
    uploading: false,
    dragActive: false,
  });
  const [specifications, setSpecifications] = useState([emptySpec()]);
  const [createdItem, setCreatedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEditing);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dirtyFields, setDirtyFields] = useState(() => new Set());

  const markDirty = useCallback((field) => {
    setDirtyFields((previous) => new Set(previous).add(field));
  }, []);

  const hydrateItem = useCallback((item) => {
    setForm({
      title: item.title || '',
      description: item.description || '',
      detail_url: item.detail_url || '',
      pic_url: item.pic_url || '',
      final_price: item.final_price ?? '',
      post_fee: item.post_fee ?? '0',
      total_stock: item.total_stock ?? '',
      max_per_user: item.max_per_user ?? '',
      status: item.status || 'active',
    });
    setSpecifications(item.specifications?.length
      ? item.specifications.map((spec) => ({
          ...spec,
          id: createRowId(),
          variants: (spec.variants || []).map((variant) => ({ ...variant, id: createRowId() })),
        }))
      : [emptySpec()]);
    const imageUrls = Array.isArray(item.images) && item.images.length
      ? item.images
      : (item.pic_url ? [item.pic_url] : []);
    setImageState({
      uploading: false,
      dragActive: false,
      files: imageUrls.map((url, index) => ({
        id: createImageId(),
        file: null,
        name: `Existing image ${index + 1}`,
        size: 0,
        preview: url,
        fileId: null,
        uploadedUrl: url,
        deleting: false,
      })),
    });
    setCreatedItem(item);
    setErrors({});
    setDirtyFields(new Set());
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const loadItem = async () => {
      setLoadingItem(true);
      try {
        const response = await getAdminPreOrderItem(itemId);
        hydrateItem(response.item);
      } catch (error) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: error?.response?.data?.message || 'Failed to load pre-order item.',
        });
      } finally {
        setLoadingItem(false);
      }
    };
    loadItem();
  }, [hydrateItem, isEditing, itemId]);

  useEffect(() => () => {
    imageState.files.forEach((fileEntry) => {
      if (fileEntry.preview) {
        URL.revokeObjectURL(fileEntry.preview);
      }
    });
  }, [imageState.files]);

  const shareLink = useMemo(() => createdItem?.share_url || '', [createdItem]);

  const uploadedImageUrls = useMemo(
    () => imageState.files.map((fileEntry) => fileEntry.uploadedUrl).filter(Boolean),
    [imageState.files]
  );

  const updateForm = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    markDirty(field);
  };

  const updateSpec = (id, field, value) => {
    markDirty('specifications');
    setSpecifications((prev) => prev.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  };

  const updateVariant = (specId, variantId, field, value) => {
    markDirty('specifications');
    setSpecifications((prev) => prev.map((spec) => {
      if (spec.id !== specId) return spec;
      return {
        ...spec,
        variants: spec.variants.map((variant) => (
          variant.id === variantId ? { ...variant, [field]: value } : variant
        )),
      };
    }));
  };

  const addVariant = (specId) => {
    markDirty('specifications');
    setSpecifications((prev) => prev.map((spec) => (
      spec.id === specId ? { ...spec, variants: [...spec.variants, emptyVariant()] } : spec
    )));
  };

  const removeVariant = (specId, variantId) => {
    markDirty('specifications');
    setSpecifications((prev) => prev.map((spec) => (
      spec.id === specId
        ? { ...spec, variants: spec.variants.filter((variant) => variant.id !== variantId) }
        : spec
    )));
  };

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setImageState((prev) => ({ ...prev, dragActive: true }));
    } else if (event.type === 'dragleave') {
      setImageState((prev) => ({ ...prev, dragActive: false }));
    }
  }, []);

  const handleFileSelect = useCallback(async (files) => {
    const uploadedFiles = [];
    const selectedFiles = Array.from(files || []).filter((file) => {
      if (!acceptedFileTypes.includes(file.type)) {
        const message = `${file.name} is not a supported image type.`;
        setErrors((prev) => ({ ...prev, image: message }));
        setSnackbar({ open: true, severity: 'error', message });
        return false;
      }
      if (file.size > maxFileSize) {
        const message = `${file.name} is larger than ${Math.round(maxFileSize / (1024 * 1024))}MB.`;
        setErrors((prev) => ({ ...prev, image: message }));
        setSnackbar({ open: true, severity: 'error', message });
        return false;
      }
      return true;
    });

    if (!selectedFiles.length) {
      return;
    }

    markDirty('images');

    const newFiles = selectedFiles.map((file) => ({
      id: createImageId(),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      fileId: null,
      uploadedUrl: '',
      deleting: false,
    }));

    setErrors((prev) => ({ ...prev, image: '' }));
    setImageState((prev) => ({
      ...prev,
      files: [...prev.files, ...newFiles],
      uploading: true,
      dragActive: false,
    }));

    try {
      for (const fileEntry of newFiles) {
        const response = await uploadManualCartImage(fileEntry.file);
        const uploadedUrl = response?.file_url || response?.url || response?.uri;
        if (!uploadedUrl) {
          throw new Error('Upload completed without returning an image URL.');
        }
        uploadedFiles.push({
          ...fileEntry,
          fileId: response?.file_id || null,
          uploadedUrl,
        });
      }

      const uploadedMap = new Map(uploadedFiles.map((fileEntry) => [fileEntry.id, fileEntry]));
      setImageState((prev) => ({
        ...prev,
        files: prev.files.map((fileEntry) =>
          uploadedMap.has(fileEntry.id)
            ? { ...fileEntry, ...uploadedMap.get(fileEntry.id) }
            : fileEntry
        ),
        uploading: false,
      }));
      setSnackbar({ open: true, severity: 'success', message: `${uploadedFiles.length} image(s) uploaded.` });
    } catch (error) {
      await Promise.all(
        uploadedFiles
          .filter((fileEntry) => fileEntry.fileId)
          .map((fileEntry) => deleteUploadedImage(fileEntry.fileId).catch(() => null))
      );
      const failedIds = new Set(newFiles.map((fileEntry) => fileEntry.id));
      newFiles.forEach((fileEntry) => URL.revokeObjectURL(fileEntry.preview));
      setImageState((prev) => ({
        ...prev,
        files: prev.files.filter((fileEntry) => !failedIds.has(fileEntry.id)),
        uploading: false,
      }));
      const message = error?.response?.data?.error || error?.message || 'Failed to upload image.';
      setErrors((prev) => ({ ...prev, image: message }));
      setSnackbar({ open: true, severity: 'error', message });
    }
  }, [markDirty]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setImageState((prev) => ({ ...prev, dragActive: false }));
    if (event.dataTransfer.files?.length) {
      handleFileSelect(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  }, [handleFileSelect]);

  const handleImageChange = useCallback((event) => {
    if (event.target.files?.length) {
      handleFileSelect(event.target.files);
    }
    event.target.value = '';
  }, [handleFileSelect]);

  const handleRemoveImage = useCallback(async (imageId) => {
    const target = imageState.files.find((fileEntry) => fileEntry.id === imageId);
    if (!target) {
      return;
    }

    setImageState((prev) => ({
      ...prev,
      files: prev.files.map((fileEntry) =>
        fileEntry.id === imageId ? { ...fileEntry, deleting: true } : fileEntry
      ),
    }));

    try {
      if (target.fileId) {
        await deleteUploadedImage(target.fileId);
      }
      if (target.preview) {
        URL.revokeObjectURL(target.preview);
      }
      setImageState((prev) => ({
        ...prev,
        files: prev.files.filter((fileEntry) => fileEntry.id !== imageId),
      }));
      markDirty('images');
      setSnackbar({ open: true, severity: 'success', message: 'Image removed.' });
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to remove image.';
      setImageState((prev) => ({
        ...prev,
        files: prev.files.map((fileEntry) =>
          fileEntry.id === imageId ? { ...fileEntry, deleting: false } : fileEntry
        ),
      }));
      setSnackbar({ open: true, severity: 'error', message });
    }
  }, [imageState.files, markDirty]);

  const usePrevious = () => {
    const saved = localStorage.getItem(previousKey);
    if (!saved) {
      setSnackbar({ open: true, severity: 'info', message: 'No previous specifications or variants saved yet.' });
      return;
    }
    try {
      const parsed = JSON.parse(saved);
      setSpecifications(parsed.specifications?.length
        ? parsed.specifications.map((item) => ({
            ...item,
            id: createRowId(),
            variants: (item.variants || []).map((variant) => ({ ...variant, id: createRowId() })),
          }))
        : [emptySpec()]);
      markDirty('specifications');
      setSnackbar({ open: true, severity: 'success', message: 'Previous specifications and variants applied.' });
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Saved specifications could not be loaded.' });
    }
  };

  const resetForm = () => {
    if (isEditing && createdItem) {
      hydrateItem(createdItem);
      setSnackbar({ open: true, severity: 'info', message: 'Saved pre-order values restored.' });
      return;
    }
    imageState.files.forEach((fileEntry) => {
      if (fileEntry.preview) {
        URL.revokeObjectURL(fileEntry.preview);
      }
    });
    setForm({
      title: '',
      description: '',
      detail_url: '',
      pic_url: '',
      final_price: '',
      post_fee: '0',
      total_stock: '',
      max_per_user: '',
      status: 'active',
    });
    setImageState({ files: [], uploading: false, dragActive: false });
    setSpecifications([emptySpec()]);
    setCreatedItem(null);
    setErrors({});
    setSnackbar({ open: true, severity: 'info', message: 'Form reset.' });
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.title.trim()) {
      nextErrors.title = 'Title is required.';
    }
    if (Number(form.post_fee || 0) < 0) {
      nextErrors.post_fee = 'Post fee cannot be negative.';
    }
    if (imageState.uploading) {
      nextErrors.image = 'Please wait for image upload to finish.';
    } else if (!uploadedImageUrls.length && !form.pic_url.trim()) {
      nextErrors.image = 'Upload at least one image or enter an image URL.';
    }
    const hasPricedSku = specifications.some((spec) => (
      spec.variants?.length
        ? spec.variants.some((variant) => Number(variant.price) > 0 && Number(variant.quantity) > 0)
        : Number(spec.price) > 0 && Number(spec.quantity) > 0
    ));
    if (!hasPricedSku) {
      nextErrors.specifications = 'Add at least one specification or variant with price and quantity.';
    }
    if (form.final_price && Number(form.final_price) <= 0) {
      nextErrors.final_price = 'Final price must be greater than zero when provided.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPreOrderItemData = ({ cleanSpecs, mainImageUrl, imageUrls }) => {
    const generatedItemId = createdItem?.item_data?.num_iid || createRandomId();
    const sellerId = createdItem?.item_data?.seller_id || createRandomId();
    const propsList = {};
    const propImgs = [];
    const skuRows = [];
    const skuMap = {};
    let minPrice = Number(form.final_price) || 0;
    let totalStock = 0;

    const variantKeyMap = new Map();
    const getVariantKey = (variant, fallbackIndex) => {
      const variantName = variant.name || 'Variant';
      const variantValue = variant.value || `Option ${fallbackIndex + 1}`;
      const mapKey = `${variantName}:${variantValue}:${variant.imageUrl || ''}`;
      if (!variantKeyMap.has(mapKey)) {
        const nextKey = `0:${variantKeyMap.size}`;
        variantKeyMap.set(mapKey, nextKey);
        propsList[nextKey] = `${variantName}:${variantValue}`;
        const imageUrl = variant.imageUrl || mainImageUrl;
        if (imageUrl) {
          propImgs.push({ properties: nextKey, url: imageUrl });
        }
      }
      return variantKeyMap.get(mapKey);
    };

    cleanSpecs.forEach((spec, specIndex) => {
      const rows = spec.variants?.length ? spec.variants : [{
        name: '',
        value: '',
        price: spec.price,
        quantity: spec.quantity,
        imageUrl: spec.imageUrl,
      }];

      rows.forEach((variant, variantIndex) => {
        const price = Number(variant.price || spec.price || form.final_price || 0);
        const quantity = Number(variant.quantity || spec.quantity || 0);
        if (price <= 0 || quantity <= 0) {
          return;
        }

        const hasVariant = Boolean(variant.name && variant.value);
        const specKey = hasVariant ? `1:${specIndex}` : `0:${specIndex}`;
        propsList[specKey] = `${spec.name}:${spec.value}`;
        if (!hasVariant && (spec.imageUrl || mainImageUrl)) {
          propImgs.push({ properties: specKey, url: spec.imageUrl || mainImageUrl });
        }

        const variantKey = hasVariant ? getVariantKey(variant, variantIndex) : null;
        const properties = hasVariant ? `${variantKey};${specKey}` : specKey;
        const propertiesName = hasVariant
          ? `${variantKey}:${variant.name}:${variant.value};${specKey}:${spec.name}:${spec.value}`
          : `${specKey}:${spec.name}:${spec.value}`;

        const skuId = createRandomId();
        const imageUrl = variant.imageUrl || spec.imageUrl || mainImageUrl;
        minPrice = minPrice ? Math.min(minPrice, price) : price;
        totalStock += quantity;
        const skuPayload = {
          sku_id: skuId,
          price,
          orginal_price: price,
          original_price: price,
          quantity,
          properties,
          properties_name: propertiesName,
          url: imageUrl,
        };
        skuRows.push(skuPayload);
        skuMap[skuId] = {
          price,
          qty: quantity,
          total_price: price * quantity,
          sku_id: skuId,
          properties,
          properties_name: propertiesName,
          url: imageUrl,
        };
      });
    });

    return {
      num_iid: generatedItemId,
      title: form.title,
      price: minPrice,
      original_price: minPrice,
      orginal_price: minPrice,
      nick: 'Checkstall Pre-order',
      min_num: 1,
      detail_url: form.detail_url || 'https://pre-order.checkstall/item',
      pic_url: mainImageUrl,
      item_imgs: imageUrls.map((url) => ({ url })),
      desc_img: imageUrls,
      post_fee: Number(form.post_fee || 0),
      seller_id: sellerId,
      priceRange: [[1, minPrice || Number(form.final_price) || 0]],
      seller_info: {
        nick: 'Checkstall Pre-order',
        title: form.title,
        shop_name: 'Checkstall Pre-order',
      },
      props_list: propsList,
      prop_imgs: { prop_img: propImgs },
      skus: { sku: skuRows },
      sku: skuMap,
      discount_price: 0,
      total_stock: totalStock,
      is_manual: true,
      is_preorder: true,
      preorder_buy_only: true,
      freight_info: null,
      productPackInfo: null,
    };
  };

  const saveItem = async () => {
    const isStatusOnlyUpdate = isEditing
      && dirtyFields.size === 1
      && dirtyFields.has('status');

    if (!isStatusOnlyUpdate && !validate()) {
      setSnackbar({ open: true, severity: 'error', message: 'Please resolve the highlighted fields.' });
      return;
    }

    setSaving(true);
    try {
      if (isStatusOnlyUpdate) {
        const response = await updateAdminPreOrderItem(itemId, { status: form.status });
        hydrateItem(response.item);
        setSnackbar({ open: true, severity: 'success', message: 'Pre-order status updated.' });
        return;
      }

      const cleanSpecs = specifications
        .filter((item) => item.name || item.value)
        .map(({ id, variants: specVariants = [], ...item }) => ({
          ...item,
          variants: specVariants
            .filter((variant) => variant.name || variant.value || variant.price || variant.quantity || variant.imageUrl)
            .map(({ id: variantId, ...variant }) => variant),
        }));
      const mainImageUrl = uploadedImageUrls[0] || form.pic_url.trim();
      const imageUrls = uploadedImageUrls.length ? uploadedImageUrls : [mainImageUrl];
      const itemData = buildPreOrderItemData({ cleanSpecs, mainImageUrl, imageUrls });
      const payload = {
        ...form,
        pic_url: mainImageUrl,
        final_price: Number(form.final_price || itemData.price || 0),
        post_fee: Number(form.post_fee || 0),
        total_stock: Number(form.total_stock || itemData.total_stock || 0),
        max_per_user: Number(form.max_per_user || 0),
        specifications: cleanSpecs,
        variants: itemData.skus.sku,
        item_data: itemData,
        images: imageUrls,
      };
      const response = isEditing
        ? await updateAdminPreOrderItem(itemId, payload)
        : await createAdminPreOrderItem(payload);
      setCreatedItem(response.item);
      setDirtyFields(new Set());
      localStorage.setItem(previousKey, JSON.stringify({ specifications: cleanSpecs }));
      setSnackbar({
        open: true,
        severity: 'success',
        message: isEditing ? 'Pre-order item updated.' : 'Pre-order item created.',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || error?.message || `Failed to ${isEditing ? 'update' : 'create'} pre-order item.`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      {loadingItem && <LinearProgress sx={{ mb: 2 }} />}
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant={isSmallScreen ? 'h5' : 'h4'} fontWeight="bold">
              {isEditing ? 'Edit Pre-order Item' : 'Create Pre-order Item'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Update the item details, availability, specifications, and variants.' : 'Add a manually prepared pre-order item. The price users see is the final fee.'}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" color="secondary" startIcon={<ContentCopy />} onClick={usePrevious}>
              Use Previous
            </Button>
            <Button variant="outlined" color="secondary" startIcon={<RestartAlt />} onClick={resetForm}>
              {isEditing ? 'Restore' : 'Reset'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/admin/pre-order/manage')}>
              Manage
            </Button>
            <Button variant="contained" startIcon={<CheckCircle />} onClick={saveItem} disabled={saving || loadingItem}>
              {saving ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Pre-order' : 'Create Pre-order')}
            </Button>
          </Stack>
        </Stack>

        {shareLink && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Share link: <strong>{shareLink}</strong>
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Item Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={form.title}
                      onChange={updateForm('title')}
                      error={Boolean(errors.title)}
                      helperText={errors.title}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select fullWidth label="Status" value={form.status} onChange={updateForm('status')}>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                      <MenuItem value="sold_out">Sold Out</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Description"
                      value={form.description}
                      onChange={updateForm('description')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Supplier Detail URL"
                      value={form.detail_url}
                      onChange={updateForm('detail_url')}
                      helperText="Optional supplier or reference page URL."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Final Price"
                      value={form.final_price}
                      onChange={updateForm('final_price')}
                      error={Boolean(errors.final_price)}
                      helperText={errors.final_price || 'This already includes service fee.'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">CNY</InputAdornment>,
                        inputMode: 'decimal',
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Post Fee"
                      value={form.post_fee}
                      onChange={updateForm('post_fee')}
                      error={Boolean(errors.post_fee)}
                      helperText={errors.post_fee}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">CNY</InputAdornment>,
                        inputMode: 'decimal',
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Total Stock"
                      value={form.total_stock}
                      onChange={updateForm('total_stock')}
                      helperText="Use 0 or leave empty for unlimited."
                      InputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Max Per User"
                      value={form.max_per_user}
                      onChange={updateForm('max_per_user')}
                      helperText="Use 0 or leave empty for no limit."
                      InputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Product Image URL"
                      value={form.pic_url}
                      onChange={updateForm('pic_url')}
                      helperText="Optional fallback if you do not upload an image."
                    />
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />

              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Item Image
                </Typography>
                <Paper
                  elevation={imageState.dragActive ? 8 : 2}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: imageState.dragActive ? '2px dashed #4CAF50' : '2px dashed #ccc',
                    backgroundColor: imageState.dragActive ? '#E8F5E8' : 'transparent',
                    transition: 'all 0.3s ease',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={acceptedFileTypes.join(',')}
                    onChange={handleImageChange}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {imageState.uploading ? 'Uploading...' : 'Upload Images'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    JPEG, PNG, GIF, or WebP. Max {Math.round(maxFileSize / (1024 * 1024))}MB per file.
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                    The first uploaded image will be used as the main pre-order image.
                  </Typography>
                </Paper>
                {imageState.uploading && (
                  <Box sx={{ width: '100%', maxWidth: 320, mt: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
                {!!imageState.files.length && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {imageState.files.map((fileEntry, index) => (
                      <Grid key={fileEntry.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card variant="outlined" sx={{ position: 'relative', height: '100%' }}>
                          <CardContent sx={{ pb: '16px !important' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                              <Chip
                                size="small"
                                color={index === 0 ? 'primary' : 'default'}
                                label={index === 0 ? 'Main Image' : `Image ${index + 1}`}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRemoveImage(fileEntry.id);
                                }}
                                disabled={fileEntry.deleting}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <Box
                              component="img"
                              src={fileEntry.preview}
                              alt={fileEntry.name}
                              sx={{
                                width: '100%',
                                height: 160,
                                objectFit: 'contain',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.default',
                                mb: 1.5,
                              }}
                            />
                            <Typography variant="body2" fontWeight={600} noWrap title={fileEntry.name}>
                              {fileEntry.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatFileSize(fileEntry.size)}
                            </Typography>
                            {fileEntry.uploadedUrl ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ wordBreak: 'break-all', display: 'block', mt: 1 }}
                              >
                                {fileEntry.uploadedUrl}
                              </Typography>
                            ) : (
                              <Chip size="small" color="warning" label="Uploading" sx={{ mt: 1 }} />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {!imageState.files.length && (
                  <Typography variant="body2" color="text.secondary">
                    No images selected yet.
                  </Typography>
                )}
                {errors.image && (
                  <Typography variant="caption" color="error" display="block" mt={1}>
                    {errors.image}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={1}
                  mb={2}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Specifications
                  </Typography>
                  <Button
                    startIcon={<AddCircleOutline />}
                    variant="contained"
                    onClick={() => {
                      markDirty('specifications');
                      setSpecifications((prev) => [...prev, emptySpec()]);
                    }}
                  >
                    Add Specification
                  </Button>
                </Stack>
                {errors.specifications && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {errors.specifications}
                  </Alert>
                )}
                <Stack spacing={2}>
                  {specifications.map((spec, index) => (
                    <Card key={spec.id} variant="outlined">
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Specification {index + 1}
                          </Typography>
                          <Box flexGrow={1} />
                          <Tooltip title="Remove specification">
                            <IconButton
                              color="error"
                              onClick={() => {
                                markDirty('specifications');
                                setSpecifications((prev) => prev.filter((item) => item.id !== spec.id));
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label="Name"
                              fullWidth
                              value={spec.name}
                              onChange={(event) => updateSpec(spec.id, 'name', event.target.value)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label="Value"
                              fullWidth
                              value={spec.value}
                              onChange={(event) => updateSpec(spec.id, 'value', event.target.value)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label="Price"
                              fullWidth
                              value={spec.price}
                              onChange={(event) => updateSpec(spec.id, 'price', event.target.value)}
                              InputProps={{ inputMode: 'decimal' }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              label="Quantity"
                              fullWidth
                              value={spec.quantity}
                              onChange={(event) => updateSpec(spec.id, 'quantity', event.target.value)}
                              InputProps={{ inputMode: 'numeric' }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              label="Specification image URL"
                              fullWidth
                              value={spec.imageUrl}
                              onChange={(event) => updateSpec(spec.id, 'imageUrl', event.target.value)}
                              helperText="Optional. Paste an image URL to use this specification image in item displays."
                            />
                          </Grid>
                        </Grid>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => addVariant(spec.id)}
                          >
                            Add Variant
                          </Button>
                          <Button
                            variant="outlined"
                            color="success"
                            startIcon={<CheckCircle />}
                            disabled={Boolean(spec.variants?.length)}
                            onClick={() => setSnackbar({ open: true, severity: 'info', message: 'SKU data will be generated automatically when you create the pre-order item.' })}
                          >
                            Generate SKU from Spec
                          </Button>
                        </Stack>
                      </CardContent>
                      {!!spec.variants?.length && (
                        <>
                          <Divider />
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              Variants
                            </Typography>
                            <Stack spacing={2}>
                              {spec.variants.map((variant, variantIndex) => (
                                <Card key={variant.id} variant="outlined">
                                  <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        Variant {variantIndex + 1}
                                      </Typography>
                                      <Box flexGrow={1} />
                                      <Tooltip title="Remove variant">
                                        <IconButton color="error" onClick={() => removeVariant(spec.id, variant.id)}>
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                    <Grid container spacing={2}>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label="Variant Name"
                                          fullWidth
                                          value={variant.name}
                                          onChange={(event) => updateVariant(spec.id, variant.id, 'name', event.target.value)}
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label="Variant Value"
                                          fullWidth
                                          value={variant.value}
                                          onChange={(event) => updateVariant(spec.id, variant.id, 'value', event.target.value)}
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label="Price"
                                          fullWidth
                                          value={variant.price}
                                          onChange={(event) => updateVariant(spec.id, variant.id, 'price', event.target.value)}
                                          InputProps={{ inputMode: 'decimal' }}
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <TextField
                                          label="Quantity"
                                          fullWidth
                                          value={variant.quantity}
                                          onChange={(event) => updateVariant(spec.id, variant.id, 'quantity', event.target.value)}
                                          InputProps={{ inputMode: 'numeric' }}
                                        />
                                      </Grid>
                                      <Grid size={{ xs: 12 }}>
                                        <TextField
                                          label="Variant image URL"
                                          fullWidth
                                          value={variant.imageUrl}
                                          onChange={(event) => updateVariant(spec.id, variant.id, 'imageUrl', event.target.value)}
                                          helperText="Optional. Paste an image URL to use this variant image in item displays."
                                        />
                                      </Grid>
                                    </Grid>
                                    <Button
                                      sx={{ mt: 2 }}
                                      variant="outlined"
                                      color="success"
                                      startIcon={<CheckCircle />}
                                      onClick={() => setSnackbar({ open: true, severity: 'info', message: 'SKU data will be generated automatically when you create the pre-order item.' })}
                                    >
                                      Generate SKU from Variant
                                    </Button>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </CardContent>
                        </>
                      )}
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PreOrderCreatePage;
